import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/apiClient";
import { useCart } from "@/contexts/CartContext";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { Heart, ShoppingCart, ArrowRight, Star, MagnifyingGlassPlus, X } from "@phosphor-icons/react";
import ProductCard from "@/components/ProductCard";
import useSeo from "@/hooks/useSeo";

export default function ProductPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [imageIdx, setImageIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [qty, setQty] = useState(1);
  const [reviews, setReviews] = useState([]);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const { addItem } = useCart();
  const { user } = useAuth();
  const { t } = useLang();

  const variants = product?.variants || [];
  const axes = product?.variant_options || [];
  const hasVariants = axes.length > 0 && variants.length > 0;
  const selectedVariant = hasVariants ? variants.find((v) => v.id === selectedVariantId) : null;

  // Effective price/stock/image = variant's own values when a variant is
  // selected, else the product's top-level values. Keeps single-SKU
  // products behaving exactly as before.
  const displayPrice = selectedVariant
    ? (selectedVariant.price != null && selectedVariant.price !== ""
        ? parseFloat(selectedVariant.price)
        : Math.round(parseFloat(selectedVariant.supplier_price || 0) * 1.30 * 100) / 100)
    : product?.price;
  const displayStock = selectedVariant ? parseInt(selectedVariant.stock || 0) : product?.stock;
  const displayImages = selectedVariant?.image
    ? [selectedVariant.image, ...(product?.images || []).filter((x) => x !== selectedVariant.image)]
    : (product?.images || []);

  useSeo({
    title: product?.name,
    description: product?.short_description || product?.description?.slice(0, 155),
    image: displayImages?.[0],
    path: `/product/${slug}`,
  });

  useEffect(() => {
    setImageIdx(0);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      // Auto-select the first in-stock variant so price/stock aren't misleading
      const vs = r.data?.variants || [];
      if ((r.data?.variant_options || []).length > 0 && vs.length > 0) {
        const inStock = vs.find((v) => (parseInt(v.stock || 0) > 0));
        setSelectedVariantId((inStock || vs[0]).id);
      } else {
        setSelectedVariantId(null);
      }
      api.get("/products", { params: { category: r.data.category, limit: 8 } }).then((res) => {
        setRelated(res.data.filter((p) => p.slug !== slug).slice(0, 4));
      });
      api.get(`/reviews/${r.data.id}`).then((res) => setReviews(res.data));
    });
  }, [slug]);

  const addToWishlist = async () => {
    if (!user) { toast.error("Please login first"); return; }
    try {
      await api.post(`/wishlist/${product.id}`);
      toast.success("Added to wishlist");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  if (!product) return <div className="max-w-[1400px] mx-auto px-6 py-24 text-center text-neutral-500">Loading...</div>;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10" data-testid="product-page">
      <div className="text-xs text-neutral-500 mb-6">
        <Link to="/" className="hover:text-black">Home</Link>{" / "}
        <Link to={`/shop/${product.category}`} className="hover:text-black">{product.category}</Link>{" / "}
        <span className="text-neutral-800">{product.name}</span>
      </div>

      <div className="grid lg:grid-cols-2 gap-10">
        <div>
          <div className="aspect-square bg-neutral-100 border border-neutral-200 mb-4 overflow-hidden relative group cursor-zoom-in" data-testid="product-main-image" onClick={() => setLightbox(true)}>
            <img src={displayImages?.[imageIdx]} alt={product.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute bottom-3 right-3 w-10 h-10 bg-white/90 backdrop-blur border border-neutral-200 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <MagnifyingGlassPlus size={18} weight="bold" />
            </div>
          </div>
          {displayImages?.length > 1 && (
            <div className="grid grid-cols-5 gap-2">
              {displayImages.map((img, i) => (
                <button
                  key={i}
                  data-testid={`thumb-${i}`}
                  onClick={() => setImageIdx(i)}
                  className={`aspect-square border ${i === imageIdx ? "border-[color:var(--vt-orange)]" : "border-neutral-200"} overflow-hidden`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="overline text-[color:var(--vt-orange)] mb-3">{product.brand}</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-4">{product.name}</h1>
          <p className="text-neutral-600 mb-6 text-lg" data-testid="product-short-description">{product.short_description}</p>
          <div className="flex items-baseline gap-3 mb-6">
            <div className="font-display font-black text-5xl" data-testid="product-price">€{(displayPrice ?? 0).toFixed(2)}</div>
            <div className={`text-xs uppercase tracking-widest font-bold ${displayStock > 0 ? "text-green-700" : "text-red-600"}`} data-testid="product-stock">
              {displayStock > 0 ? t.product.inStock : t.product.outOfStock}
            </div>
          </div>

          {hasVariants && (
            <div className="mb-6 space-y-4" data-testid="variant-selector">
              {axes.map((axis) => {
                // Distinct values for this axis, preserving product-defined order
                const values = [...new Set(variants.map((v) => v.options?.[axis]).filter(Boolean))];
                const currentValue = selectedVariant?.options?.[axis];
                return (
                  <div key={axis}>
                    <label className="overline text-neutral-600 block mb-2">{axis}{currentValue ? <span className="text-neutral-800 font-bold normal-case ml-2">: {currentValue}</span> : null}</label>
                    <div className="flex flex-wrap gap-2">
                      {values.map((val) => {
                        // Find the variant that matches all other current axes + this val
                        const target = variants.find((v) =>
                          v.options?.[axis] === val &&
                          axes.every((a) => a === axis || v.options?.[a] === selectedVariant?.options?.[a])
                        ) || variants.find((v) => v.options?.[axis] === val);
                        const disabled = !target;
                        const selected = target && target.id === selectedVariantId;
                        const oos = target && parseInt(target.stock || 0) <= 0;
                        return (
                          <button
                            key={val}
                            type="button"
                            disabled={disabled}
                            onClick={() => target && setSelectedVariantId(target.id)}
                            className={`px-4 py-2 text-xs uppercase font-bold tracking-widest border transition-colors relative ${
                              selected
                                ? "bg-black text-white border-black"
                                : oos
                                  ? "bg-white text-neutral-400 border-neutral-300 line-through"
                                  : "bg-white text-neutral-700 border-neutral-300 hover:border-black"
                            } ${disabled ? "opacity-30 cursor-not-allowed" : ""}`}
                            data-testid={`variant-pick-${axis}-${val}`}
                            title={oos ? "Out of stock" : ""}
                          >
                            {val}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {selectedVariant?.sku && (
                <div className="text-[11px] text-neutral-500" data-testid="variant-sku">SKU: <span className="font-mono">{selectedVariant.sku}</span></div>
              )}
            </div>
          )}

          <div className="flex items-center gap-3 mb-6">
            <label className="text-xs uppercase tracking-widest font-bold">{t.cart.quantity}:</label>
            <div className="flex border border-black">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-10 h-10 hover:bg-black hover:text-white transition-colors" data-testid="qty-decrease">-</button>
              <div className="w-12 h-10 flex items-center justify-center border-x border-black font-bold" data-testid="qty-value">{qty}</div>
              <button onClick={() => setQty((q) => q + 1)} className="w-10 h-10 hover:bg-black hover:text-white transition-colors" data-testid="qty-increase">+</button>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mb-8">
            <button
              disabled={displayStock <= 0 || (hasVariants && !selectedVariant)}
              onClick={() => { addItem(product, qty, selectedVariant || null); toast.success(`${qty} × ${product.name}${selectedVariant ? " · " + Object.values(selectedVariant.options || {}).filter(Boolean).join(" / ") : ""} added`); }}
              className="vt-btn-orange flex-1 min-w-[200px]"
              data-testid="pdp-add-to-cart"
            >
              <ShoppingCart size={18} weight="bold" /> {t.product.addToCart}
            </button>
            <button onClick={addToWishlist} className="vt-btn-outline" data-testid="pdp-add-wishlist">
              <Heart size={18} weight="regular" /> {t.product.wishlist}
            </button>
          </div>

          {Object.keys(product.specifications || {}).length > 0 || product.wheel_size || product.frame_size || product.color ? (
            <div className="border-t border-neutral-200 pt-6">
              <h3 className="font-display font-bold text-xl mb-3">{t.product.specifications}</h3>
              <table className="w-full text-sm" data-testid="product-specifications">
                <tbody>
                  {Object.entries(product.specifications || {}).map(([k, v]) => (
                    <tr key={k} className="border-b border-neutral-200">
                      <td className="py-2 text-neutral-500 uppercase text-xs tracking-widest">{k}</td>
                      <td className="py-2 text-right font-medium">{String(v)}</td>
                    </tr>
                  ))}
                  {product.wheel_size && <tr className="border-b border-neutral-200"><td className="py-2 text-neutral-500 uppercase text-xs tracking-widest">Wheel Size</td><td className="py-2 text-right font-medium">{product.wheel_size}</td></tr>}
                  {product.frame_size && <tr className="border-b border-neutral-200"><td className="py-2 text-neutral-500 uppercase text-xs tracking-widest">Frame Size</td><td className="py-2 text-right font-medium">{product.frame_size}</td></tr>}
                  {product.color && <tr className="border-b border-neutral-200"><td className="py-2 text-neutral-500 uppercase text-xs tracking-widest">Color</td><td className="py-2 text-right font-medium">{product.color}</td></tr>}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>

      {/* Rich sanitized description block. Backend guarantees this HTML has
          been passed through _clean_description_html() in /app/backend/scrapers.py
          — no <script>, <style>, on* handlers, or layout-only attributes. */}
      {product.description && product.description.trim() !== (product.short_description || "").trim() && (
        <div className="mt-16 border-t border-neutral-200 pt-10" data-testid="product-description-block">
          <h3 className="font-display font-bold text-2xl mb-4">Description</h3>
          <div
            className="vt-prose max-w-3xl"
            data-testid="product-description"
            dangerouslySetInnerHTML={{ __html: product.description }}
          />
        </div>
      )}

      <div className="mt-16 border-t border-neutral-200 pt-10">
        <h3 className="font-display font-bold text-2xl mb-4">{t.product.reviews}</h3>
        {reviews.length === 0 ? (
          <p className="text-neutral-500 text-sm">No reviews yet.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {reviews.map((r) => (
              <div key={r.id} className="border border-neutral-200 p-6">
                <div className="flex mb-2 text-[color:var(--vt-orange)]">
                  {[...Array(r.rating)].map((_, i) => <Star key={i} size={14} weight="fill" />)}
                </div>
                <h4 className="font-bold mb-1">{r.title}</h4>
                <p className="text-sm text-neutral-700 mb-2">{r.comment}</p>
                <div className="text-xs text-neutral-500">— {r.user_name}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {related.length > 0 && (
        <div className="mt-16 border-t border-neutral-200 pt-10">
          <h3 className="font-display font-bold text-2xl mb-6">{t.product.relatedProducts}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 bg-black/95 z-[60] flex items-center justify-center p-4 md:p-10" data-testid="lightbox" onClick={() => setLightbox(false)}>
          <button onClick={(e) => { e.stopPropagation(); setLightbox(false); }} className="absolute top-4 right-4 text-white hover:text-[color:var(--vt-orange)] z-10">
            <X size={28} weight="bold" />
          </button>
          <img
            src={product.images?.[imageIdx]}
            alt={product.name}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {product.images?.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); setImageIdx(i); }}
                  className={`w-14 h-14 border-2 ${i === imageIdx ? "border-[color:var(--vt-orange)]" : "border-white/30"} overflow-hidden`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
